import { Injectable } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { LogService } from '../logging/log.service';
import { Pages } from '../pages.config';
import jwtDecode from 'jwt-decode';
import { StaticRole } from '../../../model/roles';

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {
    LOG_SOURCE = 'AuthenticationService';
    usernameSessionKey = 'KeycloakUsername';
    userRolesSessionKey = 'KeycloakRoles';

    constructor(private readonly keycloakService: KeycloakService, private readonly logService: LogService) {}

    private setUsernameInSession(username: string) {
        sessionStorage.setItem(this.usernameSessionKey, username);
    }

    private getUsernameFromSession(): string {
        return sessionStorage.getItem(this.usernameSessionKey);
    }

    private setUserRolesInSession(userRoles: string[]) {
        if (userRoles && userRoles.length > 0) {
            sessionStorage.setItem(this.userRolesSessionKey, userRoles.join(','));
        } else {
            sessionStorage.removeItem(this.userRolesSessionKey);
        }
    }

    private getUserRolesFromSession(): string[] {
        const sessionUserRoles = sessionStorage.getItem(this.userRolesSessionKey);
        return sessionUserRoles ? sessionUserRoles.split(',') : null;
    }

    private clearSession() {
        sessionStorage.removeItem(this.usernameSessionKey);
        sessionStorage.removeItem(this.userRolesSessionKey);
    }

    login(redirectUri: string) {
        this.logService.info(this.LOG_SOURCE, 'Executing Keycloak login', { redirectUri });
        this.keycloakService.login({ redirectUri });
    }

    logout() {
        this.clearSession();
        if (window.location.href.includes(Pages.profile.router)) {
            const loginRouteWithoutSlash = Pages.login.router.replace('/', '');
            const redirectUri = window.location.href.replace(loginRouteWithoutSlash, '');
            this.logService.info(
                this.LOG_SOURCE,
                `Executing Keycloak logout on profile page with redirect uri ${redirectUri}`,
                {}
            );
            this.keycloakService.logout(redirectUri);
        } else {
            this.logService.info(this.LOG_SOURCE, `Executing Keycloak logout`, {});
            this.keycloakService.logout();
        }
    }

    checkValidToken(token: string): boolean {
        try {
            if (!token) {
                return false;
            }
            const decodedToken = jwtDecode(token) as any;
            if (!decodedToken?.resource_access?.mop?.roles?.includes(StaticRole.LOGIN)) {
                this.keycloakService.logout();
                return false;
            }
            // TODO: Here we could potentially add a check for token expiration and automatically refresh the token
            return true;
        } catch (error) {
            return false;
        }
    }

    isLoggedIn(): Promise<boolean> {
        this.logService.verbose(this.LOG_SOURCE, 'Executing Keycloak isLoggedIn', {});
        return this.keycloakService.isLoggedIn();
    }

    getUsername(): string {
        if (!this.getUsernameFromSession()) {
            this.logService.verbose(this.LOG_SOURCE, 'Executing Keycloak getUsername', {});
            this.setUsernameInSession(this.keycloakService.getUsername());
        }
        return this.getUsernameFromSession();
    }

    getUserRoles(): string[] {
        if (!this.getUserRolesFromSession()) {
            this.logService.verbose(this.LOG_SOURCE, 'Executing Keycloak getUserRoles', {});
            const roles = this.keycloakService
                .getUserRoles()
                .filter(role => role.startsWith('view-') || role.startsWith('restr_'));
            this.setUserRolesInSession(roles);
        }
        return this.getUserRolesFromSession();
    }

    async getUserProfile(): Promise<Keycloak.KeycloakProfile> {
        this.logService.verbose(this.LOG_SOURCE, 'Executing Keycloak getUserProfile', {});
        return this.keycloakService.loadUserProfile();
    }
}
